<template>
    <div class="apprentice-simple-card latest-registered-modules">
        <div class="component-title">
            <h5 class="title">Modules</h5>

            <div
                class="actions"
                v-if="!teacherAccess">
                <b-button
                    class="btn-add btn-add-module"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    :title="$t('modules.add_module')"
                    @click="showAddModuleModal">
                </b-button>
            </div>
        </div>

        <div
            class="module-list"
            v-if="moduleUsers.length">
            <div
                class="module"
                v-for="module in userSignedModules"
                :key="module.id">
                <div class="module-title">
                    {{ module.title }}
                    <span
                        class="tag-hour-count"
                        v-if="module.module_date">
                        <i class="far fa-clock time-icon"></i>
                        <div class="time">
                            <span class="time-duration">{{ module.module_date.module_period_duration }}</span>
                            <span class="time-format">{{ $t('modules.hour') }}</span>
                        </div>
                    </span>
                </div>
                <div
                    class="module-tag"
                    v-for="tag in module.module_tags"
                    :key="tag.id">
                    <i
                        class="fas fa-fw fa-circle"
                        :style="{ color: tag.color }"></i
                    >{{ tag.name }}
                </div>
            </div>

            <div
                class="show-more"
                v-if="moduleUsers.length >= 1">
                <b-button
                    class="btn-show-all btn-show-more-modules"
                    v-if="modules.length"
                    variant="primary"
                    @click="showModules">
                    {{ $t('common.more') }}
                </b-button>
            </div>
        </div>

        <div
            class="not-found with-header empty-module-list"
            v-else>
            <div
                class="btn-add-circle icon"
                v-b-tooltip.hover
                v-if="!teacherAccess"
                :title="$t('management.add_module')"
                @click="showAddModuleModal"></div>
            <div class="alert-notice">{{ $t('management.no_modules_selected') }}</div>
        </div>

        <design-2021-module-show-modules-component
            ref="module-show-modules"
            :modules-in="modulesIn"
            @update-signed-user="updateModuleUser">
        </design-2021-module-show-modules-component>
    </div>
</template>

<script>
import { ords } from '../../../app';

export default {
    name: 'design-2021-module-widget-component',
    props: {
        modulesIn: Array,
        moduleUsersIn: Array,
        teacherAccess: Boolean,
        userCredentialsIn: Object,
    },
    data() {
        return {
            modules: {},
            moduleUsers: this.moduleUsersIn,
        };
    },

    mounted() {
        // Todo: Is ORDS really needed here, there is no mutation here.
        ords.use('module').add(this.modulesIn);
        ords.use('module_users').add(this.moduleUsersIn);
        // todo dit moet automatisch met nieuwe ords/roms versie;
        ords.hold();
        for (const moduleUser of this.moduleUsersIn) {
            ords.use('module_dates').attach(moduleUser.module_date_id, 'module_users', moduleUser.id);
        }
        ords.continue();

        ords.use('module')
            .with('module_dates', (dateCallback) => {
                dateCallback.with('module_users');
                dateCallback.with('module_period', (modulePeriodsCallback) => {
                    modulePeriodsCallback.with('period');
                });
            })
            .with('file', (fileCallback) => {
                fileCallback.with('image');
            })
            .with('module_tags')
            .get()
            .subscribe((modules) => {
                this.modules = modules;
            });
    },

    methods: {
        showModuleDetailModal(moduleId) {
            this.$refs['module-show-module-detail'].show(moduleId);
        },

        showModuleUnregisterModal(module) {
            if (module.userHasMultipleRegistrations(this.$authUser.id)) {
                this.$refs['module-show-unregister'].show(module.id);
            } else {
                this.alert
                    .confirm(
                        this.$t('modules.unregistered_module'),
                        this.$t('modules.are_you_sure_you_want_to_unregister_for_this_module'),
                        this.$t('modules.yes_unregister'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        let moduleUser = module.findModuleUserByUserId(this.$authUser.id);
                        this.alert.saving();
                        this.axios.delete(this.routes.deleteModuleUser(moduleUser.id)).then(
                            (response) => {
                                moduleUser.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            }
        },

        showModules() {
            window.location.href = this.routes.showUserModules(this.userCredentialsIn.id);
        },

        getModuleDurations(module) {
            let maxDuration = 0;

            for (const duration of module.module_dates) {
                for (const moduleUser of duration.module_users) {
                    if (moduleUser.user_id === this.$authUser.id) {
                        maxDuration += duration.module_period.duration;
                    }
                }
            }

            return maxDuration;
        },

        showAddModuleModal() {
            this.$refs['module-show-modules'].show();
        },

        updateModuleUser(moduleUser) {
            const module = this.modulesIn.find((module) => module.id === moduleUser.module_id);
            const moduleDate = module.module_dates.find((moduleDate) => moduleDate.id === moduleUser.module_date_id);
            this.moduleUsers.push({
                ...moduleUser,
                module_date: moduleDate,
            });
        },
    },

    computed: {
        userSignedModules() {
            if (this.moduleUsers.length === 0) {
                return [];
            }

            return this.moduleUsers
                .filter((md) => {
                    return md.module_date;
                })
                .map((md) => {
                    md.title = md.module ? md.module.title : 'Module niet langer beschikbaar';
                    md.module_tags = md.module ? md.module.module_tags : [];
                    md.module_date.module_period_duration = md.module_date.module_period.duration;
                    return md;
                });
        },
    },
};
</script>

<style scoped lang="scss">
.latest-registered-modules {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .component-title {
        & .title + .btn {
            margin-right: 0;
        }
    }

    .module-time-chart-bar {
        margin-top: -#{map-get($grid-gutter-spacing, 'ten')};
        margin-left: -14px;
    }
}

.module-list {
    margin-top: -18px;

    .module {
        border-bottom: 1px solid $border-color;
        padding-top: map-get($grid-gutter-spacing, 'ten');
        padding-bottom: map-get($grid-gutter-spacing, 'ten');

        .module-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
            font-size: $base-font-size;
            font-weight: $font-weight-bold;
            margin-bottom: 0;

            .tag-hour-count {
                display: flex;
                align-items: center;
                align-self: flex-start;
                color: $text-muted-color;
                font-weight: $font-weight-bold;

                i {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                    margin-bottom: 0;
                    justify-self: flex-start;
                }

                .time {
                    width: 55px;
                    display: flex;
                    justify-content: flex-end;

                    .time-duration {
                        margin-right: 3px;
                        margin-bottom: 0;
                    }
                }
            }
        }

        .module-tag {
            display: flex;
            flex-direction: row;
            align-items: center;
            color: $text-muted-color;

            i {
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }
        }
    }

    :not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.show-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .btn-show-more-modules {
        font-weight: $font-weight-bold;
    }
}
</style>
